import React from "react";
import AssessmentFindings from "./AssessmentFindings";

export function MeasurementOriginNote({ result }) {
  const city = result.search_origin_city;
  const state = result.search_origin_state;
  if (!city || !state) return null;
  return (
    <div className="mt-4 pt-3 border-t border-zinc-100 text-xs text-zinc-500 leading-relaxed" data-testid="measurement-origin-note">
      <div className="uppercase tracking-wider text-[10px] font-mono text-zinc-400 mb-1">How this rank was measured</div>
      <p>
        Your ranking was measured on Google Maps from a standardized point within the{" "}
        <span className="text-zinc-700 font-medium" data-testid="measurement-origin-market">{city}, {state}</span>{" "}
        market using a consistent city-scale view. The position you see when searching Google yourself may differ because local results can vary based on your location and search context.
      </p>
    </div>
  );
}

function PositionCard({ result }) {
  const serpDown = result.serpapi_unavailable;
  const unverified = result.verification_status === "unable_to_verify";
  const canonicalOnly = result.canonical_only;
  const matched = result.map_matched;
  const rank = result.map_position;

  if (serpDown) return (
    <section className="bento p-6" data-testid="google-position-block">
      <div className="label-tiny mb-2">Where Google shows you today</div>
      <p className="text-sm text-zinc-600" data-testid="google-position-unavailable">We couldn&apos;t retrieve live Google Maps data right now. Refresh in a few minutes for the full report.</p>
    </section>
  );

  if (unverified) return (
    <section className="bento p-6" data-testid="google-position-block">
      <div className="label-tiny mb-2">Where Google shows you today</div>
      <p className="text-sm text-zinc-700" data-testid="google-position-unverified">Unable to verify your Google Business Profile — position will be reported after your listing is confirmed.</p>
      <MeasurementOriginNote result={result} />
    </section>
  );

  if (canonicalOnly) return (
    <section className="bento p-6" data-testid="google-position-block">
      <div className="label-tiny mb-2">Where Google shows you today</div>
      <p className="font-display text-xl font-bold text-zinc-900" data-testid="google-position-outside-window">Your Google Business Profile is live, but not appearing in the top 40 for &ldquo;{result.keyword}&rdquo; in {result.city}, {result.state}.</p>
      <p className="text-sm text-zinc-600 mt-2">Google knows your business exists — it just isn&apos;t recommending you for this specific search yet.</p>
      <MeasurementOriginNote result={result} />
    </section>
  );

  if (!matched) return (
    <section className="bento p-6" data-testid="google-position-block">
      <div className="label-tiny mb-2">Where Google shows you today</div>
      <p className="font-display text-xl font-bold text-zinc-900" data-testid="google-position-unranked">You&apos;re not appearing in the top 40 for &ldquo;{result.keyword}&rdquo; in {result.city}, {result.state}.</p>
      <p className="text-sm text-zinc-600 mt-2">Customers searching this exact term almost never see you.</p>
      <MeasurementOriginNote result={result} />
    </section>
  );

  return (
    <section className="bento p-6" data-testid="google-position-block">
      <div className="label-tiny mb-2">Where Google shows you today</div>
      <div className="flex items-baseline gap-3">
        <div className="font-display text-6xl lg:text-7xl font-black tabular-nums text-zinc-900" data-testid="google-position-rank">#{rank}</div>
        <div className="text-sm text-zinc-600">for &ldquo;{result.keyword}&rdquo; in {result.city}, {result.state}</div>
      </div>
      {result.share_of_attention_copy && <p className="text-sm text-zinc-600 mt-3" data-testid="share-of-attention-copy">{result.share_of_attention_copy}</p>}
      <MeasurementOriginNote result={result} />
    </section>
  );
}

/**
 * Shared Google position renderer. The full assessment path gets the new
 * facts-first findings panel before the existing position/opportunity content.
 * DiscoveryReport already renders its own findings panel, so discovery_view
 * suppresses the shared copy automatically.
 */
export function GooglePositionCard({ result, showFindings = true }) {
  const shouldShowFindings = showFindings && !result?.discovery_view;
  return (
    <div className="space-y-3">
      {shouldShowFindings && <AssessmentFindings result={result} />}
      <PositionCard result={result} />
    </div>
  );
}
